/*
 * Copyright (c) 2024. World Wide Generation Ltd
 */

import { Route, Switch, useRouteMatch } from 'react-router-dom';
import { useAppSelector } from '../../reducers';
import { SurveyRange, CustomInitiatives, CustomReportType, MetricType, CustomReportTemplate, CreateCustomReportType, CustomReport, getCustomReportBuilderKey } from '../../types/custom-report';
import { CustomReportsList } from './CustomReportsList';
import CustomReportManage from './CustomReportManage';
import CustomReportsBreadcrumbs from './CustomReportsBreadcrumbs';
import Dashboard, { DashboardRow } from '@g17eco/molecules/dashboard';
import CustomReportMetricAddForm from './CustomReportMetricAddForm';
import CustomReportMetricAddDisambiguation from './CustomReportMetricAddDisambiguation';
import { ROUTES } from '../../constants/routes';
import { CustomReportCreateDisambiguation } from './CustomReportCreateDisambiguation';
import './styles.scss';
import CustomReportManageSubsidiary from './CustomReportManageSubsidiary';
import CustomReportManageDate from './CustomReportManageDate';
import { Loader } from '@g17eco/atoms/loader';
import { FeaturePermissions } from '../../services/permissions/FeaturePermissions';
import { UpgradeRequired } from '../../routes/upgrade-required';
import { useGetCustomReportQuery } from '@api/custom-reports';
// oxlint-disable-next-line g17eco/no-restricted-paths
import { CustomReportTemplateBuilder } from '../../apps/company-tracker/components/custom-report';
import NotFound from '@g17eco/atoms/not-found';
import { getRootConfig } from '@selectors/globalData';
import { CustomReportRoutes } from './CustomReportRoutes';

const ReportManager = ({
  customReport,
  initiativeId,
}: {
  customReport: CustomReport;
  initiativeId: string;
}) => {
  switch (customReport.type) {
    case CustomReportType.Initiatives:
      return (
        <CustomReportManageSubsidiary customReport={customReport as CustomInitiatives} initiativeId={initiativeId} />
      );
    case CustomReportType.Survey:
    case CustomReportType.SurveyAggregation:
      return <CustomReportManageDate customReport={customReport as SurveyRange} initiativeId={initiativeId} />;
    case CustomReportType.Template:
      return (
        <CustomReportTemplateBuilder
          key={getCustomReportBuilderKey(initiativeId, customReport._id)}
          initiativeId={initiativeId}
          customReport={customReport as CustomReportTemplate}
        />
      );
    case CustomReportType.Metrics:
    default:
      return <CustomReportManage customReport={customReport} initiativeId={initiativeId} />;
  }
};

export const CustomReportsRoute = () => {
  const reportMatch = useRouteMatch<{
    initiativeId?: string,
    action: string,
    reportId?: string,
  }>(ROUTES.DOWNLOADS_CUSTOM);
  const reportId = reportMatch?.params.reportId;
  const initiativeId = reportMatch?.params.initiativeId;
  const action = reportMatch?.params.action;

  const rootConfig = useAppSelector(getRootConfig);
  const canAccessCombinedReport = FeaturePermissions.canAccessCombinedReport(rootConfig);

  const { data: customReport } = useGetCustomReportQuery(
    { initiativeId: initiativeId ?? '', reportId: reportId ?? '' },
    { skip: action === 'create' ||!initiativeId || !reportId }
  );

  if (!initiativeId) {
    return <NotFound />;
  }

  if (!canAccessCombinedReport) {
    return <UpgradeRequired />
  }

  // RTK Query keeps the previously-loaded report cached after navigating away. The cache is "stale" until the new
  // request resolves and replaces it; rendering the manage Switch against stale data flashes the previous report's
  // headings before the new one loads. Treat the loaded report as `undefined` until its `_id` matches the route.
  const isCreating = action === 'create';
  const isManaging = !isCreating && !!reportId;
  const loadedReport = customReport && customReport._id === reportId ? customReport : undefined;
  const isLoadingReport = isManaging && !loadedReport;
  const isCreateOrList = isCreating || !isManaging;

  return (
    <Dashboard className='custom-report-root'>
      <DashboardRow>
        <Route path={`${ROUTES.DOWNLOADS_CUSTOM.path}/:action?/:metricType?`}>
          <CustomReportsBreadcrumbs customReport={loadedReport} />
        </Route>
      </DashboardRow>
      {isLoadingReport ? (
        <Loader />
      ) : isCreateOrList ? (
        <Switch>
          <Route
            path={CustomReportRoutes.CREATE_REPORT.path}
            render={(props) => {
              const initiativeId = props.match.params.initiativeId;
              if (!initiativeId) {
                return <NotFound />;
              }
              return (
                <CustomReportCreateDisambiguation
                  initiativeId={initiativeId}
                  createType={props.match.params.createType as CreateCustomReportType | undefined}
                />
              );
            }}
          ></Route>
          <Route>
            <CustomReportsList initiativeId={initiativeId} />
          </Route>
        </Switch>
      ) : !loadedReport ? null : (
        <Switch>
          <Route
            path={CustomReportRoutes.ADD_METRIC.path}
            render={(props) => {
              if (props.match.params.metricType) {
                return (
                  <CustomReportMetricAddForm
                    type={props.match.params.metricType as MetricType}
                    customReport={loadedReport}
                  />
                );
              }
              return <CustomReportMetricAddDisambiguation customReport={loadedReport} />;
            }}
          />
          <Route path={CustomReportRoutes.MANAGE_REPORT.path}>
            <ReportManager customReport={loadedReport} initiativeId={initiativeId} />
          </Route>
          <Route>
            <CustomReportsList initiativeId={initiativeId} />
          </Route>
        </Switch>
      )}
    </Dashboard>
  );
}
